/**
 * @fileoverview Hook de UI Layer para gestión de estados de interfaz y selección de plantillas API en WhatsApp.
 * Implementa el patrón de **UI Layer** con separación estricta de responsabilidades, enfocado en gestión de estados de selección y filtros de la interfaz para plantillas API.
 * Incluye optimizaciones de performance, manejo robusto de callbacks y documentación completa de flujos y tipos de datos.
 * @module useApiTemplateUI
 * @category Hook - UI Layer
 * @since 1.0.0
 * @version 3.1.0 - Optimización de performance, documentación JSDoc completa, separación de capas y manejo avanzado de callbacks
 */
import { useState, useCallback, useMemo } from 'react';

/**
 * Hook de UI Layer para gestión de estados de interfaz y selección de plantillas API.
 */
const useApiTemplateUI = () => {
  // Estados de filtros de interfaz
  const [selectedTeam, setSelectedTeam] = useState('all');
  const [selectedTemplateType, setSelectedTemplateType] = useState('all');
  
  // Estado de selección de plantilla
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  /**
   * Selecciona equipo y resetea plantilla seleccionada. Ejecuta callback opcional.
   */
  const handleTeamSelect = useCallback((teamId, resetCallback) => {
    setSelectedTemplate(null);
    setSelectedTeam(teamId);
    resetCallback?.();
  }, []);

  /**
   * Selecciona tipo de plantilla y resetea plantilla seleccionada. Ejecuta callback opcional.
   */
  const handleTemplateTypeSelect = useCallback((typeId, resetCallback) => {
    setSelectedTemplate(null);
    setSelectedTemplateType(typeId);
    resetCallback?.();
  }, []);

  const handleIntegrationSelect = useCallback((integrationId, resetCallback) => {
    setSelectedTemplate(null);
    resetCallback?.();
    return integrationId;
  }, []);

  /**
   * Cambia la plantilla seleccionada y ejecuta callback opcional.
   */
  const handleTemplateChange = useCallback((template, changeCallback) => {
    setSelectedTemplate(template);
    changeCallback?.(template);
  }, []);

  /**
   * Resetea todos los filtros y la plantilla seleccionada. Ejecuta callback opcional.
   */
  const resetAllSelections = useCallback((completeCallback) => {
    setSelectedTeam('all');
    setSelectedTemplateType('all');
    setSelectedTemplate(null);
    completeCallback?.();
  }, []);

  /**
   * Resetea solo la plantilla seleccionada. Ejecuta callback opcional.
   */
  const resetTemplateOnly = useCallback((templateCallback) => {
    setSelectedTemplate(null);
    templateCallback?.();
  }, []);

  /**
   * Retorna el estado y handlers para la interfaz de selección de plantillas API.
   */
  return useMemo(() => ({
    selectedTeam,
    selectedTemplateType,
    selectedTemplate,
    handleTeamSelect,
    handleTemplateTypeSelect,
    handleIntegrationSelect,
    handleTemplateChange,
    resetAllSelections,
    resetTemplateOnly
  }), [
    selectedTeam,
    selectedTemplateType,
    selectedTemplate,
    handleTeamSelect,
    handleTemplateTypeSelect,
    handleIntegrationSelect,
    handleTemplateChange,
    resetAllSelections,
    resetTemplateOnly
  ]);
};

export default useApiTemplateUI;